import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Search, Filter, Archive, Bell, MessageCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Tabs, TabsContent } from '@vaiu/ui/tabs';
import { Badge } from '@vaiu/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@vaiu/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import { platformApi } from '@/services/platformApi';
import type { UnifiedMessage } from '@/types/api/user';
import ConversationView from '@/components/conversations/ConversationView';
import WhatsAppStyleMessaging from '@/components/transactions/WhatsAppStyleMessaging';
import ConversationList from '@/components/conversations/ConversationList';
import { Avatar, AvatarFallback, AvatarImage } from '@vaiu/ui/avatar';
import { formatDistanceToNow } from 'date-fns';

interface MessageThread {
  id: string;
  type: 'announcement' | 'conversation';
  merchantId: string;
  merchantName: string;
  title?: string;
  message: string;
  createdAt: string;
  readAt?: string;
  conversationId?: string;
  transactionId?: string;
  transactionAmount?: number;
  transactionCurrency?: string;
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState<MessageThread[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<MessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MessageThread | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    searchParams.get('contactId')
  );
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'announcements' | 'conversations'>('all');
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(
    searchParams.get('merchantId')
  );

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMessages();
  }, [user, navigate, selectedMerchantId]);

  useEffect(() => {
    filterMessages();
  }, [messages, searchTerm, activeTab]);

  // Handle message selection from URL
  useEffect(() => {
    const messageId = searchParams.get('messageId');
    const transactionId = searchParams.get('transactionId');
    const contactId = searchParams.get('contactId');
    
    if (messageId && messages.length > 0) {
      const message = messages.find(m => m.id === messageId);
      if (message) {
        setSelectedMessage(message);
      }
    } else if (transactionId && messages.length > 0) {
      const message = messages.find(m => m.transactionId === transactionId);
      if (message) {
        setSelectedMessage(message);
      }
    } else if (contactId) {
      // Handle direct messaging - set contactId for unified view
      setSelectedContactId(contactId);
      setSelectedMessage(null); // Clear selected message
    }
  }, [searchParams, messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use v2 contacts-summary API (ONLY endpoint that exists - per API Reference Manual)
      // Reference: API-REFERENCE-MANUAL.md line 3524
      // NOTE: There is NO v1 inbox endpoint - /api/v1/user/messages/inbox does NOT exist
      try {
        const summaryResponse = await platformApi.getContactsSummary(true); // includeRead = true to show all contacts
        
        // Transform contacts to MessageThread format
        const transformed: MessageThread[] = [];
        
        // Filter by merchant if selected
        const filteredContacts = selectedMerchantId
          ? summaryResponse.contacts.filter(c => c.contactId === selectedMerchantId)
          : summaryResponse.contacts;
        
        // Create message threads from contacts
        filteredContacts.forEach((contact) => {
          // Add conversation thread if there are messages
          if (contact.unreadMessagesCount > 0 || contact.latestMessageDate) {
            transformed.push({
              id: `conversation_${contact.contactId}`,
              type: 'conversation',
              merchantId: contact.contactId,
              merchantName: contact.contactProfile.name,
              message: `${contact.unreadMessagesCount > 0 ? `${contact.unreadMessagesCount} unread message${contact.unreadMessagesCount !== 1 ? 's' : ''}` : 'View conversation'}`,
              createdAt: contact.latestMessageDate || new Date().toISOString(),
              readAt: contact.unreadMessagesCount === 0 ? new Date().toISOString() : undefined,
              conversationId: `conv_${contact.contactId}`
            });
          }
          
          // Add announcement/post thread if there are posts
          if (contact.unreadPostsCount > 0 || contact.latestPostDate) {
            transformed.push({
              id: `post_${contact.contactId}`,
              type: 'announcement',
              merchantId: contact.contactId,
              merchantName: contact.contactProfile.name,
              title: `${contact.contactProfile.name} - Posts`,
              message: `${contact.unreadPostsCount > 0 ? `${contact.unreadPostsCount} unread post${contact.unreadPostsCount !== 1 ? 's' : ''}` : 'View posts'}`,
              createdAt: contact.latestPostDate || new Date().toISOString(),
              readAt: contact.unreadPostsCount === 0 ? new Date().toISOString() : undefined
            });
          }
        });
        
        setMessages(transformed);
      } catch (v2Error: any) {
        console.warn('⚠️ V2 contacts-summary endpoint not available:', v2Error.message);
        
        // Fallback: Try to get contacts (which uses v2 API)
        try {
          const contacts = await platformApi.contacts.getContacts({ includeTransactions: true });
          const transformed: MessageThread[] = contacts
            .filter(conv => !selectedMerchantId || conv.id === selectedMerchantId)
            .map((conv: any) => ({
              id: conv.id || conv.transactionId,
              type: 'conversation',
              merchantId: conv.id || '',
              merchantName: conv.name || 'Merchant',
              message: 'View conversation',
              createdAt: conv.lastTransactionDate || new Date().toISOString(),
              readAt: undefined,
              conversationId: conv.id,
              transactionId: conv.transactions?.[0]?.id
            }));
          setMessages(transformed);
        } catch (contactsError: any) {
          console.log('Contacts API also not available:', contactsError.message);
          setMessages([]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const filterMessages = () => {
    let filtered = [...messages];

    // Filter by tab
    if (activeTab === 'unread') {
      filtered = filtered.filter(msg => !msg.readAt);
    } else if (activeTab === 'announcements') {
      filtered = filtered.filter(msg => msg.type === 'announcement');
    } else if (activeTab === 'conversations') {
      filtered = filtered.filter(msg => msg.type === 'conversation');
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(msg =>
        msg.merchantName.toLowerCase().includes(search) ||
        msg.message.toLowerCase().includes(search) ||
        (msg.title && msg.title.toLowerCase().includes(search))
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setFilteredMessages(filtered);
  };

  const handleMessageSelect = (message: MessageThread) => {
    setSelectedMessage(message);
    
    // Mark as read if unread
    if (!message.readAt) {
      platformApi.markMessageAsRead(message.id).catch(err => {
        console.error('Failed to mark message as read:', err);
      });
    }
  };

  const handleBackToList = () => {
    setSelectedMessage(null);
    setSelectedContactId(null);
    fetchMessages(); // Refresh to update read status
  };

  const handleMarkAllRead = async () => {
    try {
      await platformApi.markAllMessagesAsRead(selectedMerchantId || undefined);
      toast({
        title: 'Success',
        description: 'All messages marked as read',
      });
      fetchMessages();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to mark all messages as read',
        variant: 'destructive',
      });
    }
  };

  const getUnreadCount = () => {
    return messages.filter(msg => !msg.readAt).length;
  };

  const getTabCounts = () => {
    const all = messages.length;
    const unread = messages.filter(m => !m.readAt).length;
    const announcements = messages.filter(m => m.type === 'announcement').length;
    const conversations = messages.filter(m => m.type === 'conversation').length;
    return { all, unread, announcements, conversations };
  };

  if (!user) return null;

  const tabCounts = getTabCounts();
  const unreadCount = getUnreadCount();

  // Get contact name from navigation state or use placeholder
  const contactName = (location.state as any)?.contactName || 'Contact';
  const transactionIdFromParams = searchParams.get('transactionId');

  // If a contactId is provided (direct messaging), show WhatsApp-style conversation view
  if (selectedContactId && !transactionIdFromParams) {
    return (
      <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex-1 flex flex-col p-2 sm:p-4 min-h-0">
          <WhatsAppStyleMessaging
            contactId={selectedContactId}
            counterpartyName={contactName}
            onBack={handleBackToList}
          />
        </div>
      </div>
    );
  }

  // If transactionId is provided via query params (with or without contactId)
  if (transactionIdFromParams) {
    // Try to get transaction details from selectedMessage if available
    const transactionAmount = selectedMessage?.transactionAmount || 0;
    const transactionCurrency = selectedMessage?.transactionCurrency || 'USD';
    const counterpartyName = selectedMessage?.merchantName || contactName;
    
    return (
      <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex-1 flex flex-col p-2 sm:p-4 min-h-0">
          <WhatsAppStyleMessaging
            transactionId={transactionIdFromParams}
            transactionAmount={transactionAmount}
            transactionCurrency={transactionCurrency}
            counterpartyName={counterpartyName}
            onBack={handleBackToList}
          />
        </div>
      </div>
    );
  }

  // If a conversation is selected (transaction-based), show WhatsApp-style conversation view
  if (selectedMessage && selectedMessage.type === 'conversation' && selectedMessage.transactionId) {
    return (
      <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex-1 flex flex-col p-2 sm:p-4 min-h-0">
          <WhatsAppStyleMessaging
            transactionId={selectedMessage.transactionId}
            transactionAmount={selectedMessage.transactionAmount || 0}
            transactionCurrency={selectedMessage.transactionCurrency || 'USD'}
            counterpartyName={selectedMessage.merchantName}
            onBack={handleBackToList}
          />
        </div>
      </div>
    );
  }

  // If an announcement is selected, show announcement view
  if (selectedMessage && selectedMessage.type === 'announcement') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="p-4">
          <div className="max-w-4xl mx-auto">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleBackToList}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Messages
            </Button>
            
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Avatar>
                    <AvatarFallback>{selectedMessage.merchantName.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle>{selectedMessage.merchantName}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(selectedMessage.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                {selectedMessage.title && (
                  <h2 className="text-xl font-semibold mt-4">{selectedMessage.title}</h2>
                )}
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                {!selectedMessage.readAt && (
                  <div className="mt-4 pt-4 border-t">
                    <Button
                      onClick={async () => {
                        await platformApi.markMessageAsRead(selectedMessage.id);
                        fetchMessages();
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Main messages list view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/')}
                className="p-2"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center space-x-2">
                  <MessageSquare className="w-6 h-6" />
                  <span>Messages</span>
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {unreadCount}
                    </Badge>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {messages.length} message{messages.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllRead}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark All Read
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSelectedMerchantId(null)}>
                    All Merchants
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Show Unread Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input 
              placeholder="Search messages..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Message Type Filter Dropdown */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <div className="flex items-center justify-between mb-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-auto justify-between">
                    <div className="flex items-center space-x-2">
                      {activeTab === 'all' && <MessageSquare className="w-4 h-4" />}
                      {activeTab === 'unread' && <Bell className="w-4 h-4" />}
                      {activeTab === 'announcements' && <Bell className="w-4 h-4" />}
                      {activeTab === 'conversations' && <MessageCircle className="w-4 h-4" />}
                      <span>
                        {activeTab === 'all' && 'All Messages'}
                        {activeTab === 'unread' && 'Unread'}
                        {activeTab === 'announcements' && 'Announcements'}
                        {activeTab === 'conversations' && 'Conversations'}
                      </span>
                      {activeTab === 'all' && (
                        <Badge variant="secondary" className="ml-1">
                          {tabCounts.all}
                        </Badge>
                      )}
                      {activeTab === 'unread' && tabCounts.unread > 0 && (
                        <Badge variant="destructive" className="ml-1">
                          {tabCounts.unread}
                        </Badge>
                      )}
                      {activeTab === 'announcements' && (
                        <Badge variant="secondary" className="ml-1">
                          {tabCounts.announcements}
                        </Badge>
                      )}
                      {activeTab === 'conversations' && (
                        <Badge variant="secondary" className="ml-1">
                          {tabCounts.conversations}
                        </Badge>
                      )}
                    </div>
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuRadioGroup value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
                    <DropdownMenuRadioItem value="all" className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4" />
                        <span>All Messages</span>
                      </div>
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.all}
                      </Badge>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="unread" className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Bell className="w-4 h-4" />
                        <span>Unread</span>
                      </div>
                      {tabCounts.unread > 0 && (
                        <Badge variant="destructive" className="ml-2">
                          {tabCounts.unread}
                        </Badge>
                      )}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="announcements" className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Bell className="w-4 h-4" />
                        <span>Announcements</span>
                      </div>
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.announcements}
                      </Badge>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="conversations" className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageCircle className="w-4 h-4" />
                        <span>Conversations</span>
                      </div>
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.conversations}
                      </Badge>
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TabsContent value={activeTab} className="mt-6">
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-8">
                      <LoadingSpinner text="Loading messages..." />
                    </div>
                  ) : error ? (
                    <div className="p-8">
                      <ErrorMessage
                        message={error}
                        onRetry={fetchMessages}
                      />
                    </div>
                  ) : filteredMessages.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No messages</h3>
                      <p className="text-muted-foreground">
                        {searchTerm.trim() 
                          ? "No messages match your search"
                          : activeTab === 'unread'
                          ? "No unread messages"
                          : activeTab === 'announcements'
                          ? "No announcements yet"
                          : activeTab === 'conversations'
                          ? "No conversations yet"
                          : "No messages yet"}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                            !message.readAt ? 'bg-blue-50/30' : ''
                          }`}
                          onClick={() => handleMessageSelect(message)}
                        >
                          <div className="flex items-start gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                {message.merchantName.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{message.merchantName}</h4>
                                  {message.type === 'announcement' && (
                                    <Badge variant="secondary" className="text-xs">
                                      <Bell className="w-3 h-3 mr-1" />
                                      Announcement
                                    </Badge>
                                  )}
                                  {message.type === 'conversation' && (
                                    <Badge variant="outline" className="text-xs">
                                      <MessageCircle className="w-3 h-3 mr-1" />
                                      Conversation
                                    </Badge>
                                  )}
                                  {!message.readAt && (
                                    <Badge variant="default" className="text-xs">New</Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                                </span>
                              </div>
                              {message.title && (
                                <p className="font-medium text-sm mb-1">{message.title}</p>
                              )}
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {message.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;

